import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDatadog } from '~/lib/hooks/useDatadog';
import { DateTime } from 'luxon';

import {
  buildFollowUpWeekBuckets,
  getCalendarWeekCount,
  getQualifyingWeekCount,
} from '../FollowUpScheduling/followUp/buckets.util';
import { buildFollowUpWindows, isFollowUpWindowExpired } from '../FollowUpScheduling/followUp/windows.util';
import { deriveFollowUpSeries } from '../FollowUpScheduling/followUp/series.util';
import { mapBookedFollowUps } from '../FollowUpScheduling/followUp/scheduleCards.util';
import { AppointmentStatus } from '~/lib/constants/calendar.constants';

export const windowKey = (w: FollowUpWindow): string =>
  `${w.startISO}|${w.endISO}`;

export type FollowUpAvailabilityProps = {
  hostId?: string;
  appointmentType?: string;
  timezone?: string;
  anchorStartISO?: string;
  anchorEndISO?: string;
  admitISO?: string;
  patientId?: string;
  skip: boolean;
};

export type WindowAvailabilityResult = {
  anchors: AvailabilityAnchorSlot[];
  isFetching: boolean;
};

const useFollowUpAvailability = ({
  hostId,
  appointmentType,
  timezone,
  anchorStartISO,
  anchorEndISO,
  admitISO,
  patientId,
  skip,
}: FollowUpAvailabilityProps) => {
  const { logError } = useDatadog();

  const anchorISO = useMemo(() => {
    const dt = anchorStartISO
      ? DateTime.fromISO(anchorStartISO, { zone: 'utc' })
      : DateTime.utc().plus({ hours: 2 }).startOf('hour');
    return dt.toUTC().toISO()!;
  }, [anchorStartISO]);

  const anchorEndUTC = useMemo(() => {
    if (!anchorEndISO) return anchorISO;
    return DateTime.fromISO(anchorEndISO, { zone: 'utc' }).toUTC().toISO() ?? anchorISO;
  }, [anchorEndISO, anchorISO]);

  const isWindowExpired = useMemo(
    () => isFollowUpWindowExpired(anchorEndUTC, timezone, undefined, admitISO),
    [anchorEndUTC, timezone, admitISO],
  );

  const [windows, setWindows] = useState<FollowUpWindow[]>([]);
  const [availByKey, setAvailByKey] = useState<
    Map<string, WindowAvailabilityResult>
  >(new Map());

  useEffect(() => {
    if (skip || !hostId || !appointmentType || isWindowExpired) {
      setWindows([]);
      setAvailByKey(new Map());
      return;
    }
    setWindows(buildFollowUpWindows(anchorISO, timezone, undefined, admitISO, anchorEndUTC));
    setAvailByKey(new Map());
  }, [admitISO, anchorEndUTC, anchorISO, appointmentType, hostId, isWindowExpired, skip, timezone]);

  const reportAvailability = useCallback(
    (key: string, result: WindowAvailabilityResult) => {
      setAvailByKey((current) => {
        const next = new Map(current);
        next.set(key, result);
        return next;
      });
    },
    [],
  );

  const reportError = useCallback(
    (error: unknown) => {
      logError(error, {
        extra: { message: 'Error fetching follow-up availability' },
        tags: { logFrom: 'useFollowUpAvailability' },
      });
    },
    [logError],
  );

  const weekCount = useMemo(
    () => getCalendarWeekCount(anchorISO, windows, timezone),
    [anchorISO, timezone, windows],
  );

  const openings = useMemo(() => {
    const all: AvailabilityAnchorSlot[] = [];
    for (const w of windows) {
      const result = availByKey.get(windowKey(w));
      if (result?.anchors?.length) all.push(...result.anchors);
    }
    return all;
  }, [availByKey, windows]);

  const buckets: FollowUpWeekBucket[] = useMemo(
    () =>
      buildFollowUpWeekBuckets(
        openings,
        anchorISO,
        weekCount,
        timezone,
        anchorEndUTC,
        admitISO,
      ),
    [openings, anchorISO, weekCount, timezone, anchorEndUTC, admitISO],
  );

  const series = useMemo(
    () => deriveFollowUpSeries(buckets, timezone),
    [buckets, timezone],
  );

  const bookableWeekCount = useMemo(
    () => getQualifyingWeekCount(buckets),
    [buckets],
  );

  const bookedByWeek = useMemo(
    () => mapBookedFollowUps([], hostId ?? '', anchorISO, weekCount, timezone),
    [hostId, anchorISO, weekCount, timezone],
  );

  const isActive = !skip && !!hostId && !!appointmentType;

  const isFetching = useMemo(() => {
    if (!isActive) return false;
    if (windows.length === 0) return true;
    const anyResolved = windows.some((w) => availByKey.has(windowKey(w)));
    return !anyResolved;
  }, [availByKey, isActive, windows]);

  return {
    windows,
    weekCount,
    bookableWeekCount,
    buckets,
    series,
    bookedByWeek,
    bookedStatuses: [AppointmentStatus.SCHEDULED, AppointmentStatus.COMPLETED],
    isFetching,
    isWindowExpired,
    appointmentType,
    hostId,
    patientId,
    reportAvailability,
    reportError,
  };
};

export default useFollowUpAvailability;
